import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Circle,
  ExternalLink,
  Flag,
  Kanban,
  ListTodo,
  Loader2,
  Pencil,
  RefreshCw,
  RotateCcw,
  SkipForward,
} from 'lucide-react';
import { toast } from 'sonner';
import { reportUiError } from '../../sentry';
import { supabase } from '../../lib/supabase';
import TaskScoreModal, { type TaskScoreModalIntent } from './TaskScoreModal';
import TaskOriginBadge from './TaskOriginBadge';
import {
  isClosedTaskStatus,
  isOpenTaskStatus,
  isTaskOverdue,
  STRATEGY_SCORE_LABELS,
  taskStatusUpdatePatch,
  taskTypeLabel,
  type NetworkingTaskRow,
  type NetworkingTaskStatus,
} from '../../lib/networkingTasks';

type QueueFilter = 'open' | 'done' | 'all';
type KindFilter = 'all' | 'transcript' | 'networking';

function formatDue(dueAt: string): string {
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return dueAt;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function contactLabel(task: NetworkingTaskRow): string {
  const sub = task.subscriber;
  const name = sub?.name?.trim();
  const email = sub?.email?.trim() || task.contact_email;
  if (name && email) return `${name} · ${email}`;
  return name || email || '—';
}

const STATUS_PILL: Record<string, string> = {
  pending: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200',
  in_progress: 'bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200',
  done: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200',
  skipped: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  canceled: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
};

const TasksView: React.FC = () => {
  const [tasks, setTasks] = useState<NetworkingTaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<QueueFilter>('open');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [scoringTask, setScoringTask] = useState<NetworkingTaskRow | null>(null);
  const [scoreIntent, setScoreIntent] = useState<TaskScoreModalIntent>('review');

  const openTaskEval = (task: NetworkingTaskRow, intent: TaskScoreModalIntent = 'review') => {
    setScoreIntent(intent);
    setScoringTask(task);
  };

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('tasks')
        .select(
          '*, subscriber:subscribers(id, name, email), transcript:transcripts(id, title, meeting_date)',
        )
        .order('created_at', { ascending: false })
        .limit(500);

      if (kindFilter === 'transcript') {
        query = query.not('transcript_id', 'is', null);
      } else if (kindFilter === 'networking') {
        query = query.is('transcript_id', null);
      }

      if (filter === 'open') {
        query = query.in('status', ['pending', 'in_progress']);
      } else if (filter === 'done') {
        query = query.in('status', ['done', 'skipped', 'canceled']);
      }

      const { data, error } = await query;
      if (error) throw error;
      setTasks((data ?? []) as NetworkingTaskRow[]);
    } catch (e: unknown) {
      reportUiError('tasks', 'list', e);
      toast.error(e instanceof Error ? e.message : 'Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [filter, kindFilter]);

  useEffect(() => {
    void fetchTasks();
  }, [fetchTasks]);

  const overdueCount = useMemo(
    () =>
      tasks.filter((t) => isTaskOverdue(t.due_at, t.status)).length,
    [tasks],
  );

  const setStatus = async (id: string, nextStatus: NetworkingTaskStatus) => {
    const task = tasks.find((t) => t.id === id);
    if (nextStatus === 'done' && task) {
      openTaskEval(task, 'complete');
      toast.message('Close-out needs value generated — what landed?');
      return;
    }

    setUpdatingId(id);
    try {
      const { error } = await supabase
        .from('tasks')
        .update(taskStatusUpdatePatch(nextStatus))
        .eq('id', id);
      if (error) throw error;
      if (nextStatus === 'pending') {
        toast.success('Task marked pending');
      }
      await fetchTasks();
    } catch (e: unknown) {
      reportUiError('tasks', 'update_status', e, { task_id: id });
      toast.error(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setUpdatingId(null);
    }
  };

  const setStrategyScore = async (id: string, score: number | null) => {
    setUpdatingId(id);
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ strategy_score: score, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      await fetchTasks();
    } catch (e: unknown) {
      reportUiError('tasks', 'update_strategy_score', e, { task_id: id });
      toast.error(e instanceof Error ? e.message : 'Failed to update strategy score');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ListTodo className="w-7 h-7 text-emerald-500" />
            Networking tasks
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
            Super Connector workflow — pre-screen before calls, enrich, LinkedIn, follow-ups.
            BookTime bookings auto-create a <strong>Pre-screen before call</strong> task (due at meeting time).
            Read.ai transcripts auto-create a <strong>Review transcript</strong> task, extract follow-ups via LLM, and build a vector index.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Link
            to="/tasks/epics"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Flag className="w-4 h-4" />
            Epics
          </Link>
          <Link
            to="/tasks/board"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Kanban className="w-4 h-4" />
            Board view
          </Link>
          <button
            type="button"
            onClick={() => void fetchTasks()}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(['open', 'done', 'all'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {f === 'open' ? 'Open' : f === 'done' ? 'Closed' : 'All'}
          </button>
        ))}
        {filter === 'open' && overdueCount > 0 ? (
          <span className="text-sm text-amber-700 dark:text-amber-300">
            {overdueCount} overdue
          </span>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(['all', 'transcript', 'networking'] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKindFilter(k)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              kindFilter === k
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {k === 'all' ? 'All types' : k === 'transcript' ? 'Transcripts' : 'Networking'}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading…
          </div>
        ) : tasks.length === 0 ? (
          <p className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            No tasks in this view.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Due
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Task
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Contact / transcript
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Origin
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Strategy
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {tasks.map((task) => {
                  const overdue = isTaskOverdue(task.due_at, task.status);
                  const busy = updatingId === task.id;
                  return (
                    <tr
                      key={task.id}
                      className={overdue ? 'bg-amber-50/80 dark:bg-amber-900/10' : ''}
                    >
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {formatDue(task.due_at)}
                        {overdue ? (
                          <span className="ml-2 text-xs font-medium text-amber-700 dark:text-amber-300">
                            overdue
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {task.title || taskTypeLabel(task.task_type)}
                        {task.notes?.trim() ? (
                          <span className="block text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                            {task.notes.trim()}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {task.transcript_id ? (
                          <Link
                            to={`/transcripts/${task.transcript_id}`}
                            className="inline-flex items-center gap-1 text-teal-600 dark:text-teal-400 hover:underline max-w-xs"
                          >
                            <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate">
                              {task.transcript?.title?.trim() || 'Open transcript'}
                            </span>
                          </Link>
                        ) : task.subscriber_id ? (
                          <Link
                            to={`/subscribers/${task.subscriber_id}`}
                            className="text-emerald-600 dark:text-emerald-400 hover:underline"
                          >
                            {contactLabel(task)}
                          </Link>
                        ) : (
                          <span className="text-gray-700 dark:text-gray-300">
                            {contactLabel(task)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <TaskOriginBadge task={task} />
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={task.strategy_score ?? ''}
                          disabled={busy}
                          title={
                            task.strategy_score
                              ? STRATEGY_SCORE_LABELS[task.strategy_score]
                              : 'Rate strategy fit (1–5)'
                          }
                          onChange={(e) => {
                            const raw = e.target.value;
                            void setStrategyScore(task.id, raw ? Number(raw) : null);
                          }}
                          className="text-xs rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-2 py-1 disabled:opacity-50"
                        >
                          <option value="">—</option>
                          {[1, 2, 3, 4, 5].map((score) => (
                            <option key={score} value={score}>
                              {score} · {STRATEGY_SCORE_LABELS[score]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${
                            STATUS_PILL[task.status] ?? STATUS_PILL.pending
                          }`}
                        >
                          {task.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1 justify-end">
                          {isOpenTaskStatus(task.status) ? (
                            <>
                              <button
                                type="button"
                                disabled={busy}
                                title="Close out — record value generated"
                                onClick={() => void setStatus(task.id, 'done')}
                                className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 disabled:opacity-50"
                              >
                                {busy ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                title="Skip"
                                onClick={() => void setStatus(task.id, 'skipped')}
                                className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                              >
                                <SkipForward className="w-4 h-4" />
                              </button>
                            </>
                          ) : isClosedTaskStatus(task.status) ? (
                            <button
                              type="button"
                              disabled={busy}
                              title="Mark pending again"
                              onClick={() => void setStatus(task.id, 'pending')}
                              className="p-1.5 rounded-md text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-50"
                            >
                              {busy ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RotateCcw className="w-4 h-4" />
                              )}
                            </button>
                          ) : null}
                          {task.transcript_id ? (
                            <Link
                              to={`/transcripts/${task.transcript_id}`}
                              title="Open transcript"
                              className="p-1.5 rounded-md text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/30"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Link>
                          ) : null}
                          <button
                            type="button"
                            disabled={busy}
                            title="Edit task"
                            onClick={() => openTaskEval(task, 'review')}
                            className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
        <Circle className="w-3 h-3" />
        Pipeline: pre-screen → review → enrich → LinkedIn → follow-up. Read.ai creates transcript review tasks.
      </p>

      {scoringTask ? (
        <TaskScoreModal
          isOpen
          task={scoringTask}
          intent={scoreIntent}
          onClose={() => setScoringTask(null)}
          onSaved={() => void fetchTasks()}
        />
      ) : null}
    </div>
  );
};

export default TasksView;
